import type { ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react'

type Variant = 'primary' | 'secondary' | 'filled' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg' | 'xl'

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-carolina-dark dark:bg-carolina text-white dark:text-navy hover:opacity-90',
  secondary:
    'border border-carolina-dark/25 dark:border-carolina/20 text-carolina-dark/80 dark:text-carolina/80 hover:border-carolina-dark dark:hover:border-carolina hover:text-carolina-dark dark:hover:text-carolina',
  filled:
    'bg-carolina text-navy border-2 border-carolina hover:bg-transparent hover:text-carolina hover:shadow-[0_0_40px_rgba(75,156,211,0.25)]',
  ghost:
    'bg-transparent text-ink dark:text-cream border-2 border-ink/15 dark:border-cream/15 hover:border-pink-dark dark:hover:border-pink hover:text-pink-dark dark:hover:text-pink',
  danger:
    'border border-red-400/30 text-red-500 dark:text-red-400 hover:bg-red-500/10 hover:border-red-400/50',
}

const sizeClasses: Record<Size, string> = {
  sm: 'text-[14px] tracking-[2px] px-3 py-1.5',
  md: 'text-[16px] tracking-[3px] px-5 py-2.5',
  lg: 'text-[16px] tracking-[3px] px-6 py-3',
  xl: 'text-[16px] tracking-[3px] px-8 sm:px-12 py-4 font-bold',
}

const baseClasses =
  'font-mono uppercase rounded-lg transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed'

type ButtonProps = {
  variant?: Variant
  size?: Size
} & ButtonHTMLAttributes<HTMLButtonElement>

type LinkButtonProps = {
  variant?: Variant
  size?: Size
  href: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: ButtonProps) {
  return (
    <button
      data-component="Button"
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    />
  )
}

export function LinkButton({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: LinkButtonProps) {
  return (
    <a
      data-component="LinkButton"
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} inline-block ${className}`}
      {...props}
    />
  )
}
